// Petits composants réutilisés par toutes les pages
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export const formatDate = (valeur, avecHeure = false) =>
  valeur
    ? new Date(valeur).toLocaleString('fr-FR', {
        dateStyle: 'short',
        ...(avecHeure && { timeStyle: 'short' }),
      })
    : '—';

/** Date de demain au format AAAA-MM-JJ : minimum des champs « expiration de licence » */
export const demain = () => new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10);

export function Chargement() {
  return <p className="muted">Chargement…</p>;
}

export function MessageErreur({ erreur }) {
  if (!erreur) return null;
  return <div className="alerte alerte-erreur">{erreur.message || String(erreur)}</div>;
}

const LIBELLES = {
  ACTIVE: 'Actif',
  INACTIVE: 'Inactif',
  AVAILABLE: 'Disponible',
  SUSPENDED: 'Fermée',
  REVOKED: 'Révoquée',
  INSTALLED: 'Installé',
  UNINSTALLED: 'Désinstallé',
  PENDING: 'En attente',
  SUCCESS: 'Réussie',
  A_JOUR: 'À jour',
  NON_A_JOUR: 'Non à jour',
};

const COULEURS = {
  ACTIVE: 'vert',
  SUCCESS: 'vert',
  A_JOUR: 'vert',
  INSTALLED: 'vert',
  AVAILABLE: 'bleu',
  PENDING: 'bleu',
  NON_A_JOUR: 'orange',
  SUSPENDED: 'orange',
  REVOKED: 'rouge',
};

/** Pastille de statut (valeur d'enum de l'API) */
export function Statut({ valeur }) {
  if (valeur === undefined || valeur === null) return <span className="muted">—</span>;
  return <span className={`badge badge-${COULEURS[valeur] || 'gris'}`}>{LIBELLES[valeur] || valeur}</span>;
}

/** Pastille de licence (poste ou logiciel) : Active seulement si la licence est réellement installée, non fermée et non expirée */
export function StatutLicence({ active, actives, total }) {
  // Compte facultatif « (1/4) » : logiciels du poste à licence active / logiciels installés
  // Avec le compte, le libellé reste « Active » : seule la couleur dit si au moins une licence est active
  const avecCompte = total !== undefined && total !== null;
  const libelle = active || avecCompte ? 'Active' : 'Inactive';
  const compte = avecCompte ? ` (${actives}/${total})` : '';
  return (
    <span className={`badge ${active ? 'badge-vert' : 'badge-orange'}`}>
      {libelle}
      {compte}
    </span>
  );
}

/** Pastille actif / inactif pour un booléen */
export const StatutBool = ({ valeur }) => <Statut valeur={valeur ? 'ACTIVE' : 'INACTIVE'} />;

export function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.pages <= 1) return null;
  const { page, pages, total } = pagination;
  return (
    <div className="pagination">
      <button className="btn btn-secondaire" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ‹ Précédent
      </button>
      <span className="muted">
        Page {page} / {pages} · {total} résultats
      </span>
      <button className="btn btn-secondaire" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Suivant ›
      </button>
    </div>
  );
}

// Fenêtres ouvertes, de la plus ancienne à la plus récente : Échap ne ferme que celle du dessus
const pileFenetres = [];

export function Modal({ titre, onFermer, children, large = false, actions = null, libelle, classe = '' }) {
  const jeton = useRef(Symbol('fenetre'));
  const fermer = useRef(onFermer);
  fermer.current = onFermer;

  useEffect(() => {
    const moi = jeton.current;
    pileFenetres.push(moi);
    const surTouche = (e) => e.key === 'Escape' && pileFenetres[pileFenetres.length - 1] === moi && fermer.current();
    window.addEventListener('keydown', surTouche);
    return () => {
      window.removeEventListener('keydown', surTouche);
      pileFenetres.splice(pileFenetres.indexOf(moi), 1);
    };
  }, []);

  return createPortal(
    <div className="modal-fond" onMouseDown={(e) => e.target === e.currentTarget && onFermer()}>
      <div
        className={`modal ${large ? 'modal-large' : ''} ${classe}`}
        role="dialog"
        aria-modal="true"
        aria-label={libelle || (typeof titre === 'string' ? titre : undefined)}
      >
        <div className="modal-entete">
          <h2>{titre}</h2>
          <div className="modal-actions">
            {actions}
            <button className="btn-icone" onClick={onFermer} aria-label="Fermer">
              ×
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Champ de formulaire avec son libellé */
export function Champ({ libelle, children, aide }) {
  return (
    <label className="champ">
      <span>{libelle}</span>
      {children}
      {aide && <small className="muted">{aide}</small>}
    </label>
  );
}

/** Saisie des informations machine d'un poste : `valeur` = { hostname, operatingSystem } */
export function ChampsMachine({ valeur, onChange }) {
  return (
    <div className="grille-2">
      <Champ libelle="Nom machine">
        <input value={valeur.hostname} onChange={(e) => onChange({ hostname: e.target.value })} placeholder="ex. PC-COMPTA-01" />
      </Champ>
      <Champ libelle="Système">
        <input
          value={valeur.operatingSystem}
          onChange={(e) => onChange({ operatingSystem: e.target.value })}
          placeholder="ex. Windows 11 Pro"
        />
      </Champ>
    </div>
  );
}

/**
 * Cases à cocher pour choisir des logiciels : `valeur` = liste d'ids.
 * Seuls les logiciels actifs sont proposés (plus ceux déjà cochés, pour pouvoir les retirer).
 */
export function ChoixLogiciels({ logiciels, valeur, onChange }) {
  const basculer = (id) => onChange(valeur.includes(id) ? valeur.filter((v) => v !== id) : [...valeur, id]);
  const proposes = logiciels.filter((l) => l.status === 'ACTIVE' || valeur.includes(l.id));
  return (
    <div className="choix-logiciels">
      {proposes.map((l) => (
        <label key={l.id} className="case">
          <input type="checkbox" checked={valeur.includes(l.id)} onChange={() => basculer(l.id)} />
          {l.name}
        </label>
      ))}
    </div>
  );
}
